import React, { useCallback, useMemo } from 'react';
import { Box, Select, MenuItem, Button, Typography, IconButton, TextField } from '@mui/material';
import { AddCircleOutline, RemoveCircleOutline, ChevronRight } from '@mui/icons-material';
import { ReactionDefinition, ReactionComponent } from '../../types/simulation';
import useSimulationStore from '../../store/simulationStore';

interface ReactionEditorLineProps {
  reactionId: string;
  onClose: () => void; // Function to close the editor panel
}

// Define styles for the standard HTML elements FIRST
const componentGroupStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap'
};
const componentBoxStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', border: '1px solid grey', borderRadius: '4px', padding: '1px', margin: '1px'
};
const countSpanStyle: React.CSSProperties = {
  minWidth: '20px', textAlign: 'center', paddingLeft: '4px', paddingRight: '4px'
};
const iconButtonStyle: React.CSSProperties = { // Basic button styling
  background: 'none', border: 'none', padding: '2px', margin: '0 2px', cursor: 'pointer', fontSize: 'inherit', color: 'inherit'
};
const selectStyle: React.CSSProperties = {
  margin: '0 4px', minWidth: '60px', background: 'inherit', color: 'inherit', border: 'none', padding: '2px'
};

// Define container style before return
const containerStyle: React.CSSProperties = {
  display: 'flex', 
  alignItems: 'center', 
  gap: '8px', // theme.spacing(1) equivalent
  padding: '8px', // theme.spacing(1) equivalent
  backgroundColor: '#333', // Approx background.paper in dark mode
  border: '1px solid grey', 
  borderRadius: '4px' // Approx theme.shape.borderRadius
};

const ReactionEditorLine: React.FC<ReactionEditorLineProps> = ({ reactionId, onClose }) => {
  // Get reaction data and update function from store
  const reaction = useSimulationStore(state => state.controls.reactions.find(r => r.id === reactionId));
  const availableSpecies = useSimulationStore(state => state.controls.chemicalSpecies);
  const setControls = useSimulationStore(state => state.setControls);
  const availableSpeciesNames = availableSpecies.map(s => s.name);

  // --- Define Callbacks and Memos *BEFORE* potential early return --- 

  // --- Update Handler (Needs to be stable for other callbacks) --- 
  const handleUpdate = useCallback((updatedReaction: Partial<ReactionDefinition>) => {
    const currentReactions = useSimulationStore.getState().controls.reactions;
    const newReactions = currentReactions.map(r => 
      r.id === reactionId ? { ...r, ...updatedReaction } : r
    );
    setControls({ reactions: newReactions }); 
  }, [reactionId, setControls]); // Depend on reactionId and setControls

  // --- Component Manipulation Functions (wrap in useCallback) ---
  const updateComponent = useCallback((
    type: 'reactants' | 'products',
    index: number,
    field: keyof ReactionComponent, 
    value: string | number
  ) => {
    if (!reaction) return; // Guard against reaction being null/undefined inside callback
    const currentComponents = reaction[type];
    const updatedComponents = [...currentComponents];
    if (field === 'name' && typeof value === 'string') {
        updatedComponents[index] = { ...updatedComponents[index], name: value };
    } else if (field === 'count' && typeof value === 'number') {
        updatedComponents[index] = { ...updatedComponents[index], count: value };
    }
    handleUpdate({ [type]: updatedComponents });
  }, [reaction, handleUpdate]); 

  const addComponent = useCallback((type: 'reactants' | 'products') => {
    if (!reaction) return;
    const defaultSpecies = availableSpeciesNames.length > 0 ? availableSpeciesNames[0] : 'Unknown'; 
    const newComponent: ReactionComponent = { name: defaultSpecies, count: 1 };
    const currentComponents = reaction[type];
    handleUpdate({ [type]: [...currentComponents, newComponent] });
  }, [reaction, availableSpeciesNames, handleUpdate]); 

  const removeComponent = useCallback((type: 'reactants' | 'products', index: number) => {
    if (!reaction) return;
    const currentComponents = reaction[type];
    const updatedComponents = currentComponents.filter((_, i) => i !== index);
    handleUpdate({ [type]: updatedComponents });
  }, [reaction, handleUpdate]); 

  const changeCount = useCallback((type: 'reactants' | 'products', index: number, delta: number) => {
    if (!reaction) return;
    const currentComponents = reaction[type];
    const currentCount = currentComponents[index].count;
    const newCount = currentCount + delta;
    if (newCount <= 0) {
      removeComponent(type, index);
    } else {
      updateComponent(type, index, 'count', newCount);
    }
  }, [reaction, removeComponent, updateComponent]); 

  // --- Render Helper (wrap in useCallback) --- 
  const renderComponentGroup = useCallback((type: 'reactants' | 'products') => {
    if (!reaction) return null; // Guard inside render helper too
    return (
      <div style={componentGroupStyle}>
        {reaction[type].map((component, index) => (
          <div key={`${type}-${index}`} style={componentBoxStyle}>
            <span style={countSpanStyle}>{component.count}</span>
            <button style={iconButtonStyle} onClick={() => changeCount(type, index, 1)} aria-label={`increase ${type} ${index}`}>+</button>
            <select
              style={selectStyle}
              value={component.name}
              onChange={(e) => updateComponent(type, index, 'name', e.target.value)}
            >
              {availableSpeciesNames.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
            <button style={iconButtonStyle} onClick={() => changeCount(type, index, -1)} aria-label={`decrease ${type} ${index}`}>-</button>
          </div>
        ))}
        <button style={iconButtonStyle} onClick={() => addComponent(type)} aria-label={`add ${type}`}>+</button>
      </div>
    );
  }, [reaction, availableSpeciesNames, changeCount, updateComponent, addComponent]); 

  // Memoize the rendered groups
  const reactantsGroup = useMemo(() => renderComponentGroup('reactants'), [renderComponentGroup]);
  const productsGroup = useMemo(() => renderComponentGroup('products'), [renderComponentGroup]);

  // --- Early Return Check --- 
  if (!reaction) {
    // We still need to call hooks above, so this check comes after them.
    // Return null or some placeholder if reaction isn't found AFTER hooks defined.
    return null; 
    // Or return <Typography color="error">Error: Reaction not found!</Typography>;
  }

  return (
    <div style={containerStyle}>
      {reactantsGroup} 
      <span style={{ margin: '0 4px' }}> { '>' } </span> 
      {productsGroup} 
    </div>
  );
};

export default ReactionEditorLine; 